import { useMemo } from 'react';
import { useGetFournisseurQuery } from 'actions/fournisseurs/queries';
import {
  useDeleteDepenseMutation,
  useUpdateDepenseMutation,
} from 'actions/depenses/mutations';
import { toast } from 'react-toastify';
import { useGetDepensesQuery } from 'actions/depenses/queries';
import { useGetEcrituresManuellesQuery } from 'actions/comptabilite/queries';
import { extractListData, formatQueryError } from 'utils/queryHelpers';

const useDepensesData = (dossier_id) => {
  const { data: depenseResponse, error: depenseError, isLoading: isLoadingDepenses, isFetching: isFetchingDepenses, refetch } =
    useGetDepensesQuery({ dossier_id });
  const { data: fournisseurResponse, error: fournisseurError, isLoading: isLoadingFournisseurs } =
    useGetFournisseurQuery({ dossier_id: dossier_id });
  const { data: ecrituresResponse, refetch: refetchEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id },
    { skip: !dossier_id }
  );

  const [updateDepense] = useUpdateDepenseMutation();
  const [deleteDepense] = useDeleteDepenseMutation();

  const updatedDonnees = useMemo(() => {
    const depenseData = extractListData(depenseResponse, depenseError, isLoadingDepenses);
    const fournisseurData = extractListData(fournisseurResponse, fournisseurError, isLoadingFournisseurs);

    const fournisseurMap = fournisseurData.reduce((acc, fournisseur) => {
      acc[fournisseur.id] = fournisseur.nom;
      return acc;
    }, {});

    // Map compte_comptable -> nom fournisseur pour les écritures manuelles
    const fournisseurByCompte = {};
    fournisseurData.forEach((f) => {
      if (f.compte_comptable) fournisseurByCompte[String(f.compte_comptable)] = f;
    });

    const sorties = depenseData.map((depense) => {
      const fournisseurNom = (typeof depense.fournisseurs === 'object' ? depense.fournisseurs?.nom : null) || fournisseurMap[depense.fournisseurs] || depense.fournisseur_nom || 'Non spécifié';
      return {
        ...depense,
        fournisseurs_details: depense.fournisseurs_details?.nom
          ? depense.fournisseurs_details
          : { nom: fournisseurNom },
        fournisseur_nom: depense.fournisseur_nom || fournisseurNom,
        fournisseurs: fournisseurNom,
      };
    });

    // IDs d'écritures déjà synchronisées sous forme de vraies Sorties en base de données
    const syncedEcritureIds = new Set(
      depenseData
        .map((d) => {
          if (!d.ecriture_comptable) return null;
          return typeof d.ecriture_comptable === 'object' ? d.ecriture_comptable.id : d.ecriture_comptable;
        })
        .filter(Boolean)
    );

    // ── Écritures manuelles non liées à une Sortie → affichées comme sorties ──
    const ecritures = ecrituresResponse?.ecritures || [];
    const ecrituresAsSorties = ecritures
      .filter((e) => {
        // Seules les écritures explicitement manuelles ou de régularisation peuvent être considérées
        if (!e.est_manuelle && !e.est_regularisation && e.source_origine !== 'MANUELLE' && e.source_origine !== 'REGULARISATION') return false;
        // Si l'écriture est liée à une sortie/entrée existante ou déjà synchronisée en base, ne pas doubler
        if (e.sortie_source || e.entree_source) return false;
        if (syncedEcritureIds.has(e.id)) return false;
        const codeCredit = String(e.code_credit || "");
        const codeDebit = String(e.code_debit || "");
        return codeCredit.startsWith("401") || codeDebit.startsWith("6") || (codeDebit.startsWith("401") && codeCredit.startsWith("5"));
      })
      .map((e) => {
        const codeCredit = String(e.code_credit || "");
        const fournisseur = codeCredit.startsWith("401") ? fournisseurByCompte[codeCredit] : null;
        const montant = Number(e.debit || e.credit || 0);
        const fNom = fournisseur ? fournisseur.nom : (codeCredit.startsWith("401") ? "Fournisseur (compte " + codeCredit + ")" : "Non spécifié");
        return {
          id: `MAN-${e.id}`,
          _source: "manuelle",
          intitule: e.libelle_original || e.libelle || "Écriture manuelle",
          fournisseurs_details: { nom: fNom },
          fournisseur_nom: fNom,
          fournisseurs: fNom,
          created_by_details: { first_name: "Saisie manuelle" },
          date_transaction: e.date || null,
          created_at: e.date || null,
          updated_at: e.date || null,
          statut_paiement: e.workflow_status === "VALIDE" ? "PAYE" : "NON_PAYE",
          prix: montant,
          montant_total: montant,
          montant_paye: e.workflow_status === "VALIDE" ? montant : 0,
          reste_a_payer: e.workflow_status === "VALIDE" ? 0 : montant,
          taxes: 0,
          piece_joint: null,
          produits_associes: [],
          archived: false,
        };
      });

    // TRI UNIFIÉ : Toujours afficher le plus récent en premier (date_transaction DESC, puis ID DESC)
    const all = [...sorties, ...ecrituresAsSorties];
    all.sort((a, b) => {
      const dateA = new Date(a.date_transaction || a.created_at || a.updated_at || 0).getTime();
      const dateB = new Date(b.date_transaction || b.created_at || b.updated_at || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      const idA = typeof a.id === 'number' ? a.id : parseInt(String(a.id).replace(/\D/g, '') || '0', 10);
      const idB = typeof b.id === 'number' ? b.id : parseInt(String(b.id).replace(/\D/g, '') || '0', 10);
      return idB - idA;
    });

    return all;
  }, [depenseResponse, fournisseurResponse, ecrituresResponse, depenseError, fournisseurError, isLoadingDepenses, isLoadingFournisseurs]);

  const queryError = depenseError ? formatQueryError(depenseError) : (fournisseurError ? formatQueryError(fournisseurError) : null);

  const handleUpdateDepense = async (depenseId, updateData) => {
    try {
      await updateDepense({ id: depenseId, ...updateData }).unwrap();
      await refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la mise à jour: ${error.data?.error || error.message}`
      );
      console.error('Request failed:', error);
    }
  };

  const handleDeleteDepense = async (depenseId) => {
    try {
      await deleteDepense(depenseId).unwrap();
      toast.success('Suppression réussie');
      await refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la suppression: ${error.data?.error || error.message}`
      );
    }
  };

  return {
    updatedDonnees,
    handleUpdateDepense,
    handleDeleteDepense,
    refetch: () => { refetch(); refetchEcritures(); },
    error: queryError,
    isLoading: isLoadingDepenses || (isFetchingDepenses && (!depenseResponse || extractListData(depenseResponse).length === 0)),
  };
};

export default useDepensesData;

